import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest, profileRequest } from "../authConfig";
import { callMsGraphGroup } from "../graphGroup";
import axios from "axios";

interface Props {
  children: React.ReactNode;
}

export const PageLayout: React.FC<Props> = (props) => {
  const { instance, accounts } = useMsal();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [initialized, setInitialized] = useState<boolean>(false);

  const request = { ...loginRequest, account: accounts[0] };
  const profileRequestOptions = { ...profileRequest, account: accounts[0] };

  useEffect(() => {
    const initializeAllData = async () => {
      try {
        // ตรวจสอบว่ามีข้อมูลครบแล้วหรือไม่
        const existingAccessToken = sessionStorage.getItem("AccessToken");
        const existingGraphToken = sessionStorage.getItem("GraphAccessToken");
        const existingUserGroup = sessionStorage.getItem("usergroup");
        const existingUserPhoto = sessionStorage.getItem("userPhoto");
        // ใช้ email จาก MSAL accounts แทนที่จะใช้จาก sessionStorage เพื่อป้องกันการ tampering
        const secureUserEmail =
          accounts && accounts.length > 0 ? accounts[0].username : null;

        // เช็คว่าข้อมูลครบหรือยัง (รูปโปรไฟล์อาจเป็น "" ถ้าไม่มี แต่ไม่ควรเป็น null)
        const hasUserPhoto = existingUserPhoto !== null;
        const hasUserGroup =
          existingUserGroup && existingUserGroup !== "undefined";

        if (
          existingAccessToken &&
          hasUserGroup &&
          hasUserPhoto &&
          secureUserEmail
        ) {
          setIsLoading(false);
          return;
        }

        // 1. Get Access Token (ถ้ายังไม่มี)
        let accessToken = existingAccessToken;
        if (!existingAccessToken) {
          const response = await instance.acquireTokenSilent(request);
          accessToken = response.accessToken;
          sessionSet("AccessToken", accessToken, 1);
        }

        // 2. Always use email from MSAL accounts for security
        if (secureUserEmail) {
          // เก็บ email ใน sessionStorage เพื่อใช้ในส่วนอื่น แต่เราจะใช้ค่าจาก MSAL เป็นหลัก
          sessionStorage.setItem("userEmail", secureUserEmail);
        }

        // 3. Get Graph Token และข้อมูล MS Graph (ถ้ายังไม่มี)
        if (!hasUserGroup || !hasUserPhoto) {
          const graphResponse = await instance.acquireTokenSilent(
            profileRequestOptions
          );
          sessionSet("GraphAccessToken", graphResponse.accessToken, 60);

          // สร้าง array ของ promises ที่จำเป็น
          const promises = [];

          if (!hasUserGroup) {
            promises.push(fetchMsGraphGroup(graphResponse.accessToken));
          }

          if (!hasUserPhoto) {
            promises.push(fetchUserPhoto(graphResponse.accessToken));
          }

          // Get user group และ photo พร้อมกัน (ถ้าจำเป็น)
          if (promises.length > 0) {
            await Promise.all(promises);
          }
        }

        setIsLoading(false);
        setInitialized(true);
      } catch (error) {
        handleError(error);
      }
    };

    // เรียกเพียงครั้งเดียวเมื่อ component mount และมี accounts
    const initPageData = async () => {
      if (!initialized && accounts.length > 0) {
        // console.log("🚀 PageLayout: Starting initialization process...");
        // console.log("🔍 PageLayout: Current sessionStorage:", {
        //   AccessToken: !!sessionStorage.getItem("AccessToken"),
        //   GraphAccessToken: !!sessionStorage.getItem("GraphAccessToken"),
        //   usergroup: !!sessionStorage.getItem("usergroup"),
        //   userPhoto: sessionStorage.getItem("userPhoto"),
        //   userEmail: !!sessionStorage.getItem("userEmail"),
        // });

        setInitialized(true); // ป้องกันการเรียกซ้ำ
        await initializeAllData();
      }
    };

    // เรียกใช้งานเมื่อ accounts พร้อมและยังไม่ได้ initialize
    if (accounts.length > 0 && !initialized) {
      initPageData();
    }
  }, [accounts.length, initialized]);

  const fetchMsGraphGroup = async (accessToken: string) => {
    try {
      const group = await callMsGraphGroup(accessToken);
      sessionStorage.setItem("usergroup", JSON.stringify(group));
    } catch (error) {
      throw error;
    }
  };

  // เพิ่มฟังก์ชันดึงรูปโปรไฟล์จาก Microsoft Graph
  const fetchUserPhoto = async (accessToken: string) => {
    try {
      const existingPhoto = sessionStorage.getItem("userPhoto");

      if (existingPhoto !== null) {
        return;
      }

      const graphEndpoint = "https://graph.microsoft.com/v1.0/me/photo/$value";
      const response = await axios.get(graphEndpoint, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        responseType: "arraybuffer",
      });

      // แปลง arraybuffer เป็น base64 สำหรับใช้ในบราวเซอร์
      const arrayBuffer = new Uint8Array(response.data);
      let binary = "";
      for (let i = 0; i < arrayBuffer.byteLength; i++) {
        binary += String.fromCharCode(arrayBuffer[i]);
      }
      const base64data = `data:image/jpeg;base64,${btoa(binary)}`;
      sessionStorage.setItem("userPhoto", base64data);
    } catch (error: unknown) {

      // Type guard to check if error is an axios error with response
      const isAxiosError = (
        err: unknown
      ): err is { response: { status: number } } => {
        if (typeof err !== "object" || err === null) {
          return false;
        }

        const errorObj = err as Record<string, unknown>;
        if (!("response" in errorObj)) {
          return false;
        }

        const response = errorObj.response;
        if (typeof response !== "object" || response === null) {
          return false;
        }

        const responseObj = response as Record<string, unknown>;
        return (
          "status" in responseObj && typeof responseObj.status === "number"
        );
      };

      if (isAxiosError(error) && error.response.status === 404) {
        sessionStorage.setItem("userPhoto", "");
      } else {
        sessionStorage.setItem("userPhoto", "");
      }
    }
  };

  const handleError = (error: unknown) => {
    // Type guard to safely access error message
    const getErrorMessage = (err: unknown): string => {
      if (typeof err === "object" && err !== null && "message" in err) {
        return (err as { message: string }).message;
      }
      return String(err);
    };

    const errorMessage = getErrorMessage(error);

    if (
      errorMessage === "AxiosError: Network Error" ||
      errorMessage === "AxiosError: Request failed with status code 500"
    ) {
      setIsLoading(false);
      // ไม่ reload อัตโนมัติเพื่อป้องกัน infinite loop
    }

    if (errorMessage === "AxiosError: Request failed with status code 400") {
      setIsLoading(false);
    }
  };

  const sessionSet = (
    key: string,
    value: string,
    expirationInMin: number = 10
  ) => {
    const expirationDate = new Date(
      new Date().getTime() + 60000 * expirationInMin
    );
    const newValue = {
      value: value,
      expirationDate: expirationDate.toISOString(),
    };
    sessionStorage.setItem(key, JSON.stringify(newValue));
  };

  return (
    <>
      {isLoading ? (
        <div className="layout-loading">
          <div className="lds-ring">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      ) : (
        props.children
      )}
    </>
  );
};
